// src/lib/geste-empreinte.js — RECONNAÎTRE UN GESTE DÉJÀ PRÉPARÉ, SANS LE RELIRE.
//
// ⚠️ SOURCE UNIQUE pour le serveur ET l'écran (F10 phase 2, 03/10). Le
// navigateur POSE l'empreinte, le serveur la COMPARE : une seule copie, donc
// plus aucune divergence possible. Écrit en CommonJS pur : l'écran l'importe
// directement ; le serveur le lit par le relais `lib/geste-empreinte.js`.
// Ne rien ajouter qui ne soit chargeable des deux côtés (garde :
// src/lib/doublons-lib-conformite.test.js, « sources uniques »).
//
// ══ POURQUOI (2026-09-15, chantier « l'exécution réelle ») ═════════════════
//
// Depuis que « oui » vaut le clic, une carte reste en attente pendant que la
// conversation continue. Deux défauts deviennent alors possibles, et tous deux
// sont silencieux :
//   · le modèle RECOMPOSE le même geste au tour suivant (« je prépare ça » sur
//     quelque chose qui est déjà préparé) — Dimitri voit une seconde carte
//     identique et ne sait plus laquelle son « oui » va lancer ;
//   · le modèle repropose un geste qui VIENT D'ÊTRE FAIT.
//
// Pour l'éviter, le serveur doit savoir quels gestes sont en attente ou faits.
// Mais il ne doit PAS pour autant recevoir leurs arguments : le corps d'un
// message au voyageur (4 000 caractères) n'a aucune raison de refaire le
// voyage. D'où une EMPREINTE — une chaîne courte, opaque, dérivée du levier et
// de ses arguments, qui permet de comparer sans rien relire.
//
// ⚠️ CE N'EST PAS UNE SIGNATURE DE SÉCURITÉ. Elle n'authentifie rien : le fil
// reste une déclaration du navigateur. Elle ne sert qu'à RETIRER une carte en
// double — le sens le plus sûr : au pire, un geste légitime n'est pas reproposé
// et Dimitri le redemande en une phrase.

// Dix minutes : le temps qu'une carte reste pertinente à l'écran. Au-delà, la
// conversation a bougé, et reproposer le geste est la bonne réponse.
const FENETRE_GESTE_MS = 10 * 60 * 1000;

/**
 * Les clés triées, récursivement — deux objets égaux donnent le même texte.
 *
 * ⚠️ ET LES NOMBRES DEVIENNENT DES CHAÎNES (revue adverse #1432, mineur 5).
 * Le même geste peut arriver avec `price_eur: 40` ou `price_eur: "40"` selon
 * que le modèle a écrit un nombre ou l'a cité : deux empreintes différentes
 * pour un geste identique, donc une carte en double qu'on croyait écartée.
 * Les booléens suivent la même règle, pour la même raison.
 */
function _canon(v) {
  if (Array.isArray(v)) return v.map(_canon);
  if (v && typeof v === "object") {
    const out = {};
    for (const k of Object.keys(v).sort()) out[k] = _canon(v[k]);
    return out;
  }
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  return v;
}

/** FNV-1a 32 bits — court, déterministe, sans dépendance ni crypto. */
function _hash(texte) {
  let h = 0x811c9dc5;
  for (let i = 0; i < texte.length; i += 1) {
    h ^= texte.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(16).padStart(8, "0");
}

/**
 * L'empreinte d'un geste : `<levier>#<hash des arguments>`.
 * @returns {string|null} `null` si le geste n'a pas de levier nommable.
 */
function empreinteDeGeste(geste) {
  const nom =
    geste && typeof geste.levier === "string" ? geste.levier.trim() : "";
  if (!nom) return null;
  let args = "{}";
  try {
    args = JSON.stringify(_canon((geste && geste.args) || {}));
  } catch {
    // Un argument circulaire (impossible depuis un JSON, mais gratuit à
    // couvrir) : on retombe sur le levier seul plutôt que de lever.
    args = "?";
  }
  return `${nom}#${_hash(args)}`;
}

/**
 * Les empreintes des gestes préparés ou exécutés RÉCEMMENT, lues dans le fil
 * que le navigateur a envoyé.
 *
 * @param {Array} fil — `body.fil`, tel quel
 * @param {number} maintenant — l'horloge de l'appelant (jamais `Date.now()`
 *   ici : ce module doit rester testable sans horloge)
 * @returns {string[]}
 */
function empreintesRecentes(fil, maintenant) {
  const tours = Array.isArray(fil) ? fil : [];
  const ref = Number.isFinite(maintenant) ? maintenant : 0;
  const out = new Set();
  for (const t of tours) {
    if (!t || typeof t !== "object") continue;
    const quand = typeof t.quand === "number" ? t.quand : null;
    // Sans heure, on ne sait pas si c'est récent : on ne retire rien.
    if (quand === null || ref - quand > FENETRE_GESTE_MS || ref < quand)
      continue;
    // ⚠️ UN GESTE QUI A ÉCHOUÉ N'A PAS ÉTÉ FAIT (seconde revue #1436). On
    // ramassait l'empreinte de TOUS les comptes rendus, sans regarder l'issue
    // — donc un geste refusé en 409 devenait « déjà préparé » et ne pouvait
    // plus être reproposé. Sur un formulaire, cela veut dire : neuf champs
    // dictés, un conflit de version, et plus aucun moyen de réessayer.
    //
    // ⚠️ MAIS `incertain` RESTE RAMASSÉ, et la nuance est la seule qui compte
    // ici : « on ne sait pas si c'est parti » n'est pas « ça n'est pas
    // parti ». Reproposer à l'identique un geste qui a peut-être créé un
    // propriétaire, c'est pousser à en créer un second — et un doublon dans
    // ce KV, c'est une facture au mauvais nom. Seul le refus FRANC rouvre.
    const issue =
      t.role === "fait" && t.fait && typeof t.fait === "object"
        ? t.fait.issue
        : null;
    if (issue === "refuse") continue;
    const e =
      t.role === "lui"
        ? t.geste_empreinte
        : t.role === "fait" && t.fait && typeof t.fait === "object"
          ? t.fait.empreinte
          : null;
    if (typeof e === "string" && e && e.length <= 200) out.add(e);
  }
  return [...out];
}

module.exports = {
  FENETRE_GESTE_MS,
  empreinteDeGeste,
  empreintesRecentes,
  _internal: { _canon, _hash },
};
